/**
 * miniMarkdown.js
 * A small, dependency-free Markdown -> HTML renderer for AI output. It escapes
 * HTML first (safe against injection), then applies a practical subset of
 * Markdown: headings, bold/italic, inline + fenced code, lists, blockquotes,
 * links and paragraphs. Fenced code blocks get a language class so the AI CSS
 * can apply lightweight syntax styling.
 */

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function inline(text) {
  let out = text;
  // Inline code first so its contents are not further formatted.
  out = out.replace(/`([^`]+)`/g, (_, c) => `<code class="ai-inline-code">${c}</code>`);
  // Bold, then italic.
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
  out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  // Links [text](url)
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  // Bare URLs
  out = out.replace(
    /(^|[\s(])((https?:\/\/)[^\s<)]+)/g,
    '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>'
  );
  return out;
}

export function renderMarkdown(markdown) {
  if (!markdown) return '';
  const escaped = escapeHtml(markdown);
  const lines = escaped.split('\n');
  const html = [];
  let inCode = false;
  let codeLang = '';
  let codeBuffer = [];
  let listType = null; // 'ul' | 'ol'
  let listItems = [];

  const flushList = () => {
    if (!listType) return;
    html.push(`<${listType} class="ai-md-list">${listItems.join('')}</${listType}>`);
    listType = null;
    listItems = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    const fence = line.match(/^```(\w*)\s*$/);
    if (fence) {
      if (inCode) {
        html.push(
          `<pre class="ai-code-block"><code class="ai-lang-${codeLang || 'text'}">${codeBuffer.join('\n')}</code></pre>`
        );
        inCode = false;
        codeBuffer = [];
        codeLang = '';
      } else {
        flushList();
        inCode = true;
        codeLang = fence[1] || '';
      }
      continue;
    }

    if (inCode) {
      codeBuffer.push(line);
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      flushList();
      const level = heading[1].length;
      html.push(`<h${level} class="ai-md-h">${inline(heading[2])}</h${level}>`);
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      flushList();
      html.push(`<blockquote class="ai-md-quote">${inline(line.replace(/^\s*>\s?/, ''))}</blockquote>`);
      continue;
    }

    const ulItem = line.match(/^\s*[-*+]\s+(.*)$/);
    const olItem = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (ulItem || olItem) {
      const type = ulItem ? 'ul' : 'ol';
      if (listType && listType !== type) flushList();
      listType = type;
      listItems.push(`<li>${inline((ulItem || olItem)[1])}</li>`);
      continue;
    }

    if (/^\s*$/.test(line)) {
      flushList();
      continue;
    }

    flushList();
    html.push(`<p class="ai-md-p">${inline(line)}</p>`);
  }

  if (inCode && codeBuffer.length) {
    html.push(`<pre class="ai-code-block"><code>${codeBuffer.join('\n')}</code></pre>`);
  }
  flushList();

  return html.join('\n');
}

export default renderMarkdown;
